<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { Chart, type ChartConfiguration, type ChartDataset, type TooltipItem } from 'chart.js/auto'
import dayjs from 'dayjs'
import { formatMoneyWithSymbol } from '@/lib/money'
import { sideConfig } from '@/lib/product'
import { INSIGHTS_SIDES, sideColor, sideConfigKey, type InsightsChart } from '@/lib/insights'

/**
 * #1 Trend Average per side as lines and #6 active listings per side as bars in a
 * band of their own below them.
 *
 * x is a time axis, so the gap a missing point leaves is bridged in proportion to the
 * time it spans: the ticket's linear interpolation (#10), for display only, the API
 * never fills a point in.
 */
const props = defineProps<{
    chart: InsightsChart
    currencySymbol: string
}>()

const { t, locale } = useI18n()

const canvas = ref<HTMLCanvasElement | null>(null)
let instance: Chart | null = null

const GRID = 'rgba(156, 163, 175, 0.2)'
const TICKS = 'rgba(156, 163, 175, 1)'

const hasPoints = computed(() =>
    INSIGHTS_SIDES.some((side) => (props.chart[side] ?? []).some((point) => point.trend_average !== null)),
)

type Point = { x: number; y: number | null }

const datasets = (): ChartDataset<'line' | 'bar', Point[]>[] =>
    INSIGHTS_SIDES.flatMap((side) => {
        const points = props.chart[side]

        if (points === null) {
            return []
        }

        const label = sideConfig[sideConfigKey(side)].label

        return [
            {
                type: 'line' as const,
                label: `${label} · ${t('insights.chart.trendAverage')}`,
                data: points.map((point) => ({ x: dayjs(point.date).valueOf(), y: point.trend_average })),
                yAxisID: 'y',
                borderColor: sideColor(side),
                backgroundColor: sideColor(side),
                borderWidth: 2,
                pointRadius: points.length > 40 ? 0 : 2.5,
                pointHoverRadius: 4,
                spanGaps: true,
                tension: 0,
                order: 0,
            },
            {
                type: 'bar' as const,
                label: `${label} · ${t('insights.chart.listings')}`,
                data: points.map((point) => ({ x: dayjs(point.date).valueOf(), y: point.listings })),
                yAxisID: 'listings',
                backgroundColor: sideColor(side, 0.35),
                borderWidth: 0,
                maxBarThickness: 8,
                order: 1,
            },
        ]
    })

/**
 * The band's top is the busiest day, not the next round tick: rounding 5,200 up to
 * 10,000 would leave the bars filling half of an already short band.
 */
const listingsMax = (): number | undefined => {
    const listings = INSIGHTS_SIDES.flatMap((side) => (props.chart[side] ?? []).map((point) => point.listings))
    const busiest = Math.max(0, ...listings)

    return busiest === 0 ? undefined : Math.ceil(busiest * 1.05)
}

const RANGE_DAYS: Record<InsightsChart['range'], number | null> = { '90d': 90, '1y': 365, all: null }

/**
 * The x window is the range itself, ending on the week's Monday. Left to Chart.js, a
 * bar series on a linear axis widens the window to fit its bars: two points on dev
 * stretched the axis from 2023 to 2030.
 */
const xBounds = (): { min: number | undefined; max: number | undefined } => {
    const dates = INSIGHTS_SIDES.flatMap((side) =>
        (props.chart[side] ?? []).map((point) => dayjs(point.date).valueOf()),
    )

    if (dates.length === 0) {
        return { min: undefined, max: undefined }
    }

    const end = props.chart.week === null ? Math.max(...dates) : dayjs(props.chart.week).valueOf()
    const days = RANGE_DAYS[props.chart.range]
    const start = days === null ? Math.min(...dates) : dayjs(end).subtract(days, 'day').valueOf()
    const pad = Math.max((end - start) * 0.01, 86_400_000)

    return { min: start - pad, max: end + pad }
}

const render = () => {
    if (canvas.value === null) {
        return
    }

    if (instance !== null) {
        instance.data.datasets = datasets()
        instance.options.scales!.listings!.max = listingsMax()
        Object.assign(instance.options.scales!.x!, xBounds())
        instance.update()

        return
    }

    const config: ChartConfiguration<'line' | 'bar', Point[]> = {
        type: 'line',
        data: { datasets: datasets() },
        options: {
            responsive: true,
            maintainAspectRatio: false,
            interaction: { mode: 'nearest', axis: 'x', intersect: false },
            plugins: {
                // The view draws its own legend: one entry per side, not per series.
                legend: { display: false },
                tooltip: {
                    callbacks: {
                        title: (items: TooltipItem<'line' | 'bar'>[]) =>
                            dayjs(Number(items[0].parsed.x)).format('DD MMM YYYY'),
                        label: (item: TooltipItem<'line' | 'bar'>) => {
                            const value = item.parsed.y

                            if (item.dataset.yAxisID === 'listings') {
                                return `${item.dataset.label}: ${Math.round(value ?? 0).toLocaleString(locale.value)}`
                            }

                            return `${item.dataset.label}: ${formatMoneyWithSymbol(value, props.currencySymbol)}`
                        },
                    },
                },
            },
            scales: {
                x: {
                    type: 'linear',
                    ...xBounds(),
                    border: { display: false },
                    grid: { color: GRID },
                    ticks: {
                        color: TICKS,
                        maxTicksLimit: 8,
                        callback: (value: string | number) => dayjs(Number(value)).format('DD/MM/YY'),
                    },
                },
                // Prices and listings stack on the left, the listings bars confined to
                // the bottom third as in the mockup, so they never run behind the lines.
                // Chart.js lays left-side boxes out bottom-up in definition order.
                listings: {
                    position: 'left',
                    stack: 'insights',
                    stackWeight: 1,
                    beginAtZero: true,
                    max: listingsMax(),
                    border: { display: false },
                    // No ticks: their top label collided with the price axis's lowest one,
                    // and the tooltip gives each bar's count.
                    grid: { display: false },
                    ticks: { display: false },
                },
                y: {
                    position: 'left',
                    stack: 'insights',
                    stackWeight: 2,
                    grace: '5%',
                    border: { display: false },
                    grid: { color: GRID },
                    ticks: {
                        color: TICKS,
                        callback: (value: string | number) => `${props.currencySymbol} ${value}`,
                    },
                },
            },
        },
    }

    instance = new Chart(canvas.value, config)
}

onMounted(render)

watch(() => props.chart, render)

onBeforeUnmount(() => {
    instance?.destroy()
    instance = null
})
</script>

<template>
    <div class="relative h-96">
        <canvas ref="canvas" />
        <div v-if="!hasPoints" class="absolute inset-0 flex items-center justify-center text-sm text-muted-foreground">
            {{ t('insights.chart.empty') }}
        </div>
    </div>
</template>
